import { Cancel } from "@mui/icons-material";
import { Autocomplete, styled, useTheme } from "@mui/material";
import type { TextFieldProps, Theme } from "@mui/material";
import { Checkbox, Chip, TextField, Typography } from "@mui/material";
import React, { useRef } from "react";

const StyledCheckbox = styled(Checkbox)({
  paddingTop: 0,
  paddingBottom: 0,
});

const StateIcon = styled("div")(({ theme }) => ({
  width: theme.spacing(2.5),
  height: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius,
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(2),
}));

export interface StateSelectorProps<T extends string> {
  /** The currently selected states */
  value: T[];
  /** Callback when the selected states change */
  onChange: (states: T[]) => void;
  /** The available state options */
  options: readonly T[];
  /** Returns the background color for a state */
  getStateColor: (state: string, palette: Theme["palette"]) => string;
  /** Label for the input field */
  label?: string;
  /** Placeholder when no states are selected */
  placeholder?: string;
}

function StateSelectorInner<T extends string>({
  value,
  onChange,
  options,
  getStateColor,
  label = "State",
  placeholder = "Any state",
}: StateSelectorProps<T>) {
  const theme = useTheme();

  const hasFocus = useRef(false);
  const searchUpdatedSinceFocus = useRef(false);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      value={value}
      options={[...options]}
      onChange={(_, newValue: T[]) => {
        onChange(newValue);
        if (hasFocus.current) {
          searchUpdatedSinceFocus.current = true;
        }
      }}
      autoComplete
      autoHighlight
      renderInput={(props: TextFieldProps) => (
        <TextField
          {...props}
          label={label}
          variant="outlined"
          placeholder={value.length ? "" : placeholder}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...props.inputProps,
            "data-1p-ignore": true,
            "data-lpignore": "true",
          }}
          onFocus={() => {
            hasFocus.current = true;
            searchUpdatedSinceFocus.current = false;
          }}
          onBlur={() => {
            hasFocus.current = false;
          }}
        />
      )}
      renderOption={(props, state, { selected }) => (
        <li {...props} key={state}>
          <StyledCheckbox checked={selected} color="primary" />
          <StateIcon
            style={{
              backgroundColor: getStateColor(state, theme.palette),
            }}
          />
          <span>{state}</span>
        </li>
      )}
      renderTags={(tagValue: T[], getTagProps) => {
        const { key, ...tagProps } = getTagProps({ index: 0 });
        return (
          <>
            <Chip
              key={key}
              color="primary"
              variant="outlined"
              deleteIcon={<Cancel />}
              label={
                <Typography variant="body2" color="primary" noWrap>
                  {tagValue[0]}
                </Typography>
              }
              {...tagProps}
            />
            {tagValue.length > 1 && (
              <Chip
                color="primary"
                label={
                  <Typography variant="body2" color="primary">
                    {`+${tagValue.length - 1}`}
                  </Typography>
                }
                variant="outlined"
              />
            )}
          </>
        );
      }}
      sx={{
        width: theme.spacing(35),
        "& input": {
          minWidth: "0 !important",
        },
      }}
    />
  );
}

const StateSelector = React.memo(
  StateSelectorInner,
) as typeof StateSelectorInner;

export { StateSelector };
